import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react'
import type {
    MatchDetails,
    StatsPerformCountries,
    StatsPerformCountry,
} from '../utils/statsperform/types'
import { fetchMatchDetails, getFetchFrequency } from '../utils/statsperform/statsperformUtils'
import { FETCH_FREQUENCY } from '../config/statsperformConfig'
import { useEditor } from '@smartfire/js-block-engine'
import { fetchWithTimeout } from '../shared/apiFetchWithTimeout'
import { CONSTS } from '../config/const'

type Match = {
    matchId: string
    updating: boolean
    error: boolean
    data: MatchDetails | undefined
}

type DispatchAction = {
    matchId: string
} & (
    | {
          field: 'error'
          value: boolean
      }
    | {
          field: 'updating'
          value: boolean
      }
    | {
          field: 'data'
          value: MatchDetails
      }
    | {
          subscribe: boolean
      }
)

const matchesReducer = (
    matches: Match[],
    action: { matchId: string } & (
        | { field: 'error'; value: boolean }
        | { field: 'updating'; value: boolean }
        | { field: 'data'; value: MatchDetails }
        | { subscribe: boolean }
    ),
) => {
    const { matchId } = action

    if ('subscribe' in action) {
        const { subscribe } = action

        const existed = matches.some((m) => m.matchId === matchId)

        if (subscribe) {
            return existed
                ? matches
                : [...matches, { matchId, error: false, updating: false, data: undefined }]
        }

        return existed ? matches.filter((m) => m.matchId !== matchId) : matches
    }

    const { field, value } = action

    return matches.map((m) => (m.matchId === matchId ? { ...m, [field]: value } : m))
}

const FetchMatch = ({
    fetcherParam,
    fetcher,
    frequency,
}: {
    fetcherParam: string
    fetcher: (x: string) => void
    frequency: number
}) => {
    useEffect(() => {
        fetcher(fetcherParam)
    }, [fetcher, fetcherParam])

    useEffect(() => {
        const timer = setInterval(() => {
            fetcher(fetcherParam)
        }, frequency + Math.random() * 500)

        return () => {
            clearInterval(timer)
        }
    }, [fetcher, fetcherParam, frequency])

    return null
}

interface InitialMatchData {
    statsperformid: string
    initial_json: string
}

interface Props {
    children: React.ReactNode
    /**
     * default matches to subscribe
     */
    joinedMatchInitialData?: InitialMatchData[]
}

type StatsPerformMatchesContextValue = {
    matches: Match[]
    dispatch: React.Dispatch<DispatchAction>
    countries: StatsPerformCountry[]
}

// @ts-ignore
const StatsPerformMatchesContext = createContext<StatsPerformMatchesContextValue>(null)

const parseInitialJson = (str: string) => {
    try {
        const data = JSON.parse(str)
        if (
            !data.matchInfo ||
            !data.matchInfo.competition ||
            !data.matchInfo.contestant ||
            !data.matchInfo.date ||
            !data.matchInfo.date ||
            !data.matchInfo.round ||
            !data.matchInfo.time ||
            !data.matchInfo.tournamentCalendar ||
            !data.matchInfo.venue
        ) {
            return undefined // Should not occur but we prevent white page if object seems bad
        }
        return data
    } catch {
        return undefined
    }
}

const initMatches = (joinedMatchInitialData?: InitialMatchData[]): Match[] => {
    if (!joinedMatchInitialData) return []
    return joinedMatchInitialData.map((match) => ({
        matchId: match.statsperformid,
        error: false,
        updating: false,
        data: match.initial_json ? parseInitialJson(match.initial_json) : undefined,
    }))
}

export const StatsPerformMatchesProvider = ({ joinedMatchInitialData, children }: Props) => {
    const [matches, dispatch] = useReducer(matchesReducer, initMatches(joinedMatchInitialData))
    const [countries, setCountries] = useState<StatsPerformCountry[]>([])

    useEffect(() => {
        fetchWithTimeout<StatsPerformCountries>(`${CONSTS.FLAGS_FOLDER}/mappings.json`).then(
            (data) => {
                if (data.countries) {
                    setCountries(data.countries)
                }
            },
        )
    }, [])

    const fetchData = useCallback(async (matchId: string) => {
        if (!matchId) return

        // Stop fetching if document is not visible
        if (document.visibilityState !== 'visible') return
        dispatch({ matchId, field: 'updating', value: true })

        try {
            const match = await fetchMatchDetails(matchId)
            dispatch({ matchId, field: 'data', value: match })
            dispatch({ matchId, field: 'error', value: false })
        } catch (e) {
            dispatch({ matchId, field: 'error', value: true })
        } finally {
            dispatch({ matchId, field: 'updating', value: false })
        }
    }, [])

    const value = useMemo(() => ({ matches, dispatch, countries }), [matches, countries])

    return (
        <StatsPerformMatchesContext.Provider value={value}>
            {matches.map((m) => {
                const frequency = m.data ? getFetchFrequency(m.data) : FETCH_FREQUENCY.liveMatch // default to .liveMatch
                return (
                    <FetchMatch
                        key={m.matchId}
                        fetcherParam={m.matchId}
                        fetcher={fetchData}
                        frequency={frequency}
                    />
                )
            })}
            {children}
        </StatsPerformMatchesContext.Provider>
    )
}

export const useStatsPerformMatchesContext = () => {
    const context = useContext(StatsPerformMatchesContext)
    const { isEditing } = useEditor()
    if (isEditing) {
        return {
            matches: [],
            dispatch: () => null,
            countries: [],
        }
    }
    if (context === null) {
        throw new Error('You are using useStatsPerformMatchesContext without the provider')
    }
    return context
}

export const useStatsPerformMatch = (matchId: string | undefined) => {
    const { matches, dispatch } = useStatsPerformMatchesContext()

    const match = matches.find((m) => m.matchId === matchId)

    useEffect(() => {
        if (!matchId) return
        dispatch({ matchId, subscribe: true })
    }, [dispatch, matchId])

    return match
}
